import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import { has_access } from "../../../middleware/privileges";
import { lang } from "../../../middleware/lang";
import { toDate } from "../../../middleware/dates";

const VIEW_ORDER_HISTORY_PERMISSION = 262144;

const GET_ORDER_EDIT_HISTORY = gql`
  query getOrderEditHistory($order_id: Int!) {
    getOrderEditHistory(order_id: $order_id) {
      id
      action
      subuser_id
      subuser_name
      total_before
      total_after
      products_added {
        product_id
        title
        price
        qty
      }
      products_removed {
        product_id
        title
        price
        qty
      }
      discount_amount
      discount_percent
      created_at
    }
  }
`;

const OrderEditHistory = ({ order_id }) => {
  const [open, setOpen] = useState(false);
  const orderId = parseInt(order_id, 10);

  if (!has_access(VIEW_ORDER_HISTORY_PERMISSION)) return null;

  return (
    <div>
      <button
        className="btn btn-link px-0 text-dark font-weight-bold"
        onClick={() => setOpen((v) => !v)}
        style={{ textDecoration: "none" }}
      >
        <i className="fas fa-history mr-2"></i>
        {lang("Historial de cambios", "Edit History")}
        <i className={`fas fa-chevron-${open ? "up" : "down"} ml-2 text-muted small`}></i>
      </button>

      {open && <HistoryPanel order_id={orderId} />}
    </div>
  );
};

const HistoryPanel = ({ order_id }) => {
  const { data, loading, error } = useQuery(GET_ORDER_EDIT_HISTORY, {
    variables: { order_id },
    fetchPolicy: "cache-and-network",
  });

  if (loading) {
    return <p className="text-muted small mt-2">{lang("Cargando...", "Loading...")}</p>;
  }

  if (error) {
    return (
      <p className="text-danger small mt-2">
        {lang("Error al cargar historial", "Error loading history")}
      </p>
    );
  }

  const entries = data?.getOrderEditHistory || [];

  return (
    <div className="mt-2">
      <p className="text-muted small mb-2">
        <i className="fas fa-info-circle mr-1"></i>
        {lang(
          "Los registros de cambios se conservan por 15 días.",
          "Change records are kept for 15 days only."
        )}
      </p>

      {entries.length === 0 ? (
        <p className="text-muted small">
          {lang("Sin cambios registrados.", "No changes recorded.")}
        </p>
      ) : (
        entries.map((entry) => <HistoryEntry key={entry.id} entry={entry} />)
      )}
    </div>
  );
};

const ACTION_LABELS = {
  fulfilled:   { es: "Cerrado",    en: "Closed",       badgeClass: "badge-success",   icon: "fa-check-circle" },
  cancelled:   { es: "Cancelado",  en: "Cancelled",    badgeClass: "badge-danger",    icon: "fa-times-circle" },
  reactivated: { es: "Reactivado", en: "Reactivated",  badgeClass: "badge-secondary", icon: "fa-undo" },
  charged:     { es: "Cobrado",    en: "Charged",      badgeClass: "badge-secondary", icon: "fa-dollar-sign" },
  tip:         { es: "Propina",    en: "Tip",          badgeClass: "badge-secondary", icon: "fa-hand-holding-usd" },
  discount:    { es: "Descuento",  en: "Discount",     badgeClass: "badge-warning",   icon: "fa-percent" },
};

const HistoryEntry = ({ entry }) => {
  const totalDiff = parseFloat(entry.total_after) - parseFloat(entry.total_before);
  const diffColor = totalDiff < 0 ? "text-danger" : totalDiff > 0 ? "text-success" : "text-muted";
  const diffSign = totalDiff > 0 ? "+" : "";
  const actionMeta = ACTION_LABELS[entry.action] || null;
  const isDiscount = entry.action === "discount";
  const isStatusChange =
    entry.action !== "edit" && entry.action !== "tip" && !isDiscount;
  const isTip = entry.action === "tip";

  return (
    <div className="border-bottom pb-2 mb-2">
      <div className="d-flex justify-content-between align-items-start">
        <div>
          {actionMeta && (
            <span className={`badge ${actionMeta.badgeClass} mr-1`}>
              <i className={`fas ${actionMeta.icon} mr-1`}></i>
              {lang(actionMeta.es, actionMeta.en)}
            </span>
          )}
          <span className="font-weight-bold small">
            {entry.subuser_name || lang("Administrador", "Administrator")}
          </span>
          <br />
          <span className="text-muted" style={{ fontSize: "0.75rem" }}>
            {toDate(entry.created_at)}
          </span>
        </div>
        {!isStatusChange && !isTip && (
          <div className="text-right">
            <span className="text-muted small">
              ${parseFloat(entry.total_before).toFixed(2)}
              <i className="fas fa-arrow-right mx-1 text-muted"></i>
              ${parseFloat(entry.total_after).toFixed(2)}
            </span>
            {totalDiff !== 0 && (
              <>
                <br />
                <span className={`small font-weight-bold ${diffColor}`}>
                  {diffSign}${Math.abs(totalDiff).toFixed(2)}
                  {isDiscount && entry.discount_percent != null
                    ? ` (${parseFloat(entry.discount_percent).toFixed(1)}%)`
                    : ""}
                </span>
              </>
            )}
          </div>
        )}
        {isTip && (
          <span className="text-success small align-self-center font-weight-bold">
            +${parseFloat(entry.total_after).toFixed(2)}
          </span>
        )}
        {isStatusChange && (
          <span className="text-muted small align-self-center">
            ${parseFloat(entry.total_before).toFixed(2)}
          </span>
        )}
      </div>

      {entry.products_removed.length > 0 && (
        <div className="mt-1">
          <span className="badge badge-danger mr-1">
            <i className="fas fa-minus mr-1"></i>
            {lang("Eliminados", "Removed")}
          </span>
          {entry.products_removed.map((p, i) => (
            <span key={i} className="text-danger small mr-2">
              {p.qty > 1 ? `${p.qty}x ` : ""}{p.title} (${parseFloat(p.price).toFixed(2)})
            </span>
          ))}
        </div>
      )}

      {entry.products_added.length > 0 && (
        <div className="mt-1">
          <span className="badge badge-success mr-1">
            <i className="fas fa-plus mr-1"></i>
            {lang("Agregados", "Added")}
          </span>
          {entry.products_added.map((p, i) => (
            <span key={i} className="text-success small mr-2">
              {p.qty > 1 ? `${p.qty}x ` : ""}{p.title} (${parseFloat(p.price).toFixed(2)})
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrderEditHistory;
